/**
 * The `/judge` hero console (`EX-PUB19`): an example submission for Metro Card
 * walked through the judge's own steps, Queued, Compiling, Running test by
 * test, Accepted. An illustration of the product, not live traffic: the page
 * says so in the console's accessible name, and the numbers on it are the ones
 * a correct solution to this problem plausibly gets.
 *
 * Pure, so the timeline and the tokenizer are tested without a browser.
 */

export type ConsoleLanguage = "cpp" | "python";

export type TokenKind = "pp" | "kw" | "ty" | "fn" | "st" | "nu" | "op" | "id" | "ns" | "ws";
export type ConsoleToken = { text: string; kind: TokenKind };
export type ConsoleLine = { no: number; tokens: ConsoleToken[] };

type ConsoleSource = {
  file: string;
  toolchain: string;
  build: string;
  name: string;
  perf: string;
  lines: readonly string[];
};

/** Both programs are correct solutions to Metro Card; the two samples print "70 1" and "0 1". */
export const CONSOLE_SOURCES: Readonly<Record<ConsoleLanguage, ConsoleSource>> = {
  cpp: {
    file: "metro-card.cpp",
    toolchain: "C++17 · GCC 12.2.0",
    build: "g++ -std=c++17",
    name: "C++17",
    perf: "0.09 s · 3.1 MB",
    lines: [
      "#include <bits/stdc++.h>",
      "using namespace std;",
      "",
      "int main() {",
      "    long long n, f, m, b; int q;",
      "    cin >> n >> f >> m >> b >> q;",
      "    int refused = 0;",
      "    while (q--) {",
      "        char e; cin >> e;",
      "        if (e == 'R') { long long x; cin >> x; b += x; }",
      "        else {",
      "            long long a, c; cin >> a >> c;",
      "            long long fare = max(m, llabs(a - c) * f);",
      "            if (b >= fare) b -= fare; else refused++;",
      "        }",
      "    }",
      "    cout << b << ' ' << refused << '\\n';",
      "}",
    ],
  },
  python: {
    file: "metro_card.py",
    toolchain: "Python 3.11 · CPython",
    build: "CPython 3.11",
    name: "Python 3.11",
    perf: "0.31 s · 12.4 MB",
    lines: [
      "import sys",
      "",
      "def main():",
      "    data = sys.stdin.buffer.read().split()",
      "    n, f, m, b = map(int, data[:4])",
      "    q, i, refused = int(data[4]), 5, 0",
      "    for _ in range(q):",
      "        if data[i] == b'R':",
      "            b += int(data[i + 1]); i += 2",
      "        else:",
      "            a, c = int(data[i + 1]), int(data[i + 2]); i += 3",
      "            fare = max(m, abs(a - c) * f)",
      "            if b >= fare: b -= fare",
      "            else: refused += 1",
      "    print(b, refused)",
      "",
      "main()",
    ],
  },
};

/** Both panes are this tall whatever the language, so switching never moves the page. */
export const CONSOLE_LINE_COUNT = 18;

const KEYWORDS: Readonly<Record<ConsoleLanguage, Readonly<Record<string, TokenKind>>>> = {
  cpp: {
    using: "kw", namespace: "kw", while: "kw", if: "kw", else: "kw", return: "kw",
    int: "ty", long: "ty", char: "ty",
    std: "ns",
    cin: "fn", cout: "fn", max: "fn", llabs: "fn", main: "fn",
  },
  python: {
    import: "kw", def: "kw", for: "kw", in: "kw", if: "kw", else: "kw", return: "kw",
    sys: "ns",
    map: "fn", int: "fn", max: "fn", abs: "fn", range: "fn", print: "fn", main: "fn",
  },
};

const TOKEN_PATTERN = /(\s+)|(b?'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")|(\d+)|([A-Za-z_]\w*)|([^\sA-Za-z0-9_'"]+)/g;

export function tokenizeLine(line: string, language: ConsoleLanguage): ConsoleToken[] {
  if (language === "cpp" && /^\s*#/.test(line)) return [{ text: line, kind: "pp" }];
  const keywords = KEYWORDS[language];
  const tokens: ConsoleToken[] = [];
  for (const match of line.matchAll(TOKEN_PATTERN)) {
    const [text, space, string, number, word] = match;
    let kind: TokenKind = "op";
    if (space) kind = "ws";
    else if (string) kind = "st";
    else if (number) kind = "nu";
    else if (word) kind = keywords[word] ?? "id";
    tokens.push({ text, kind });
  }
  return tokens;
}

export function consoleLines(language: ConsoleLanguage): ConsoleLine[] {
  const source = CONSOLE_SOURCES[language].lines;
  return Array.from({ length: CONSOLE_LINE_COUNT }, (_, index) => ({
    no: index + 1,
    tokens: tokenizeLine(source[index] ?? "", language),
  }));
}

/* ── The run ────────────────────────────────────────────────────────────── */

export type ConsolePhase = "ready" | "queued" | "compiling" | "running" | "accepted";

/** Two samples, then ten hidden tests: what the real verdict panel reports. */
export const CONSOLE_SAMPLES = 2;
export const CONSOLE_HIDDEN = 10;
export const CONSOLE_TESTS = CONSOLE_SAMPLES + CONSOLE_HIDDEN;

/**
 * Milliseconds from the start of a run. Every step is 150 to 250 ms or a
 * short hold, and the whole run lands inside five seconds, which is why it may
 * start on its own (`docs/03` §7, showcase motion).
 */
export const CONSOLE_TIMELINE = {
  pressFrom: 450,
  queued: 700,
  compiling: 1300,
  running: 2100,
  perTest: 200,
  accepted: 4500,
} as const;

export type ConsoleState = {
  phase: ConsolePhase;
  /** Tests finished, 0 to {@link CONSOLE_TESTS}. */
  testsDone: number;
  /** The Submit button is showing its pressed state (the automatic first run only). */
  pressing: boolean;
};

/** `null` means no run has started for the file on screen. */
export function consoleState(elapsedMs: number | null): ConsoleState {
  if (elapsedMs === null || elapsedMs < CONSOLE_TIMELINE.queued) {
    return {
      phase: "ready",
      testsDone: 0,
      pressing: elapsedMs !== null && elapsedMs >= CONSOLE_TIMELINE.pressFrom,
    };
  }
  if (elapsedMs < CONSOLE_TIMELINE.compiling) return { phase: "queued", testsDone: 0, pressing: false };
  if (elapsedMs < CONSOLE_TIMELINE.running) return { phase: "compiling", testsDone: 0, pressing: false };
  if (elapsedMs < CONSOLE_TIMELINE.accepted) {
    const done = Math.floor((elapsedMs - CONSOLE_TIMELINE.running) / CONSOLE_TIMELINE.perTest);
    return { phase: "running", testsDone: Math.min(CONSOLE_TESTS, done), pressing: false };
  }
  return { phase: "accepted", testsDone: CONSOLE_TESTS, pressing: false };
}
